import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, AlertCircle, Clock, CheckCircle, Archive, Send, ChevronDown, X, User, Building2 } from 'lucide-react';
import { useAppContext, Ticket } from '../../context/AppContext';
import { supabase } from '../../lib/supabase';
export function TicketManager() {
  const {
    tickets,
    updateTicket,
    staff
  } = useAppContext();
  const [filter, setFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [view, setView] = useState<'active' | 'archive'>('active');
  const [expandedTicket, setExpandedTicket] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [departments, setDepartments] = useState<any[]>([]);
  useEffect(() => {
    async function loadDepartments() {
      try {
        const {
          data,
          error
        } = await supabase.from('site_settings').select('value').eq('key', 'departments').maybeSingle();
        if (!error && data?.value) {
          setDepartments(data.value as any[]);
        }
      } catch (err) {
        console.error('Failed to load departments', err);
      }
    }
    loadDepartments();
  }, []);
  const activeTickets = tickets.filter(t => t.status !== 'closed');
  const archivedTickets = tickets.filter(t => t.status === 'closed');
  const displayTickets = view === 'active' ? activeTickets : archivedTickets;
  const filteredTickets = displayTickets.filter(t => {
    const matchesStatus = filter === 'all' || t.status === filter;
    const matchesDept = deptFilter === 'all' || t.department === deptFilter;
    return matchesStatus && matchesDept;
  });
  const statusCounts = {
    all: activeTickets.length,
    open: activeTickets.filter(t => t.status === 'open').length,
    'in-progress': activeTickets.filter(t => t.status === 'in-progress').length,
    resolved: activeTickets.filter(t => t.status === 'resolved').length
  };
  const handleStatusChange = (ticketId: string, newStatus: Ticket['status']) => {
    updateTicket(ticketId, {
      status: newStatus,
      updatedAt: new Date().toISOString()
    });
    if (newStatus === 'closed') {
      setExpandedTicket(null);
    }
  };
  const handleReply = (ticketId: string) => {
    if (!replyText.trim()) return;
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket) return;
    const newMessage = {
      id: `m${Date.now()}`,
      sender: 'Admin Staff',
      role: 'staff' as const,
      content: replyText.trim(),
      createdAt: new Date().toISOString()
    };
    updateTicket(ticketId, {
      messages: [...ticket.messages, newMessage],
      updatedAt: new Date().toISOString(),
      status: ticket.status === 'open' ? 'in-progress' : ticket.status
    });
    setReplyText('');
  };
  const handleAssign = (ticketId: string, staffId: string) => {
    updateTicket(ticketId, {
      assignedTo: staffId,
      updatedAt: new Date().toISOString()
    });
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'text-red-500 bg-red-500/10 border-red-500/20';
      case 'high':
        return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
      case 'medium':
        return 'text-amber-500 bg-amber-500/10 border-amber-500/20';
      default:
        return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'text-red-400 bg-red-500/10 border-red-500/20';
      case 'in-progress':
        return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
      case 'resolved':
        return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
      case 'closed':
        return 'text-slate-400 bg-slate-700/30 border-slate-600/30';
      default:
        return 'bg-slate-800 text-slate-300 border-slate-700';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertCircle size={14} data-id="element-1451" />;
      case 'in-progress':
        return <Clock size={14} data-id="element-1452" />;
      case 'resolved':
        return <CheckCircle size={14} data-id="element-1453" />;
      case 'closed':
        return <Archive size={14} data-id="element-1454" />;
      default:
        return <MessageSquare size={14} data-id="element-1455" />;
    }
  };
  const getStaffName = (staffId?: string) => {
    if (!staffId) return 'Unassigned';
    const member = staff.find(s => s.id === staffId);
    return member ? member.name : 'Unknown';
  };
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="space-y-8" data-id="element-1456">
      <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} data-id="element-1457">
        <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-1458">
          Support Tickets
        </h1>
        <p className="text-slate-400" data-id="element-1459">
          Manage customer inquiries and technical support.
        </p>
      </motion.div>

      {/* View Toggle: Active vs Archive */}
      <div className="flex items-center gap-4" data-id="element-1460">
        <div className="flex gap-2 bg-slate-950 p-1 rounded-xl border border-slate-800" data-id="element-1461">
          <button onClick={() => setView('active')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${view === 'active' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'}`} data-id="element-1462">
            Active
          </button>
          <button onClick={() => setView('archive')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${view === 'archive' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'}`} data-id="element-1463">
            <Archive size={16} data-id="element-1464" /> Archive
          </button>
        </div>

        <div className="flex gap-2" data-id="element-1465">
          <div className="relative" data-id="element-1466">
            <select value={filter} onChange={e => setFilter(e.target.value)} className="bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-fox-500 appearance-none pr-10" data-id="element-1467">
              <option value="all" data-id="element-1468">All Statuses ({statusCounts.all})</option>
              <option value="open" data-id="element-1469">Open ({statusCounts.open})</option>
              <option value="in-progress" data-id="element-1470">
                In Progress ({statusCounts['in-progress']})
              </option>
              <option value="resolved" data-id="element-1471">
                Resolved ({statusCounts.resolved})
              </option>
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-1472" />
          </div>

          {departments.length > 0 && <div className="relative" data-id="element-1473">
              <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} className="bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-fox-500 appearance-none pr-10" data-id="element-1474">
                <option value="all" data-id="element-1475">All Departments</option>
                {departments.map(d => <option key={d.id} value={d.id} data-id="element-1476">
                    {d.name}
                  </option>)}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-1477" />
            </div>}
        </div>
      </div>

      {/* Status Filters (active view only) */}
      {view === 'active' && <div className="flex gap-2 flex-wrap" data-id="element-1478">
          {(['all', 'open', 'in-progress', 'resolved'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${filter === f ? 'bg-fox-600 text-white' : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white'}`} data-id="element-1479">
              {f === 'all' ? 'All' : f === 'in-progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${filter === f ? 'bg-white/20' : 'bg-slate-800'}`} data-id="element-1480">
                {statusCounts[f]}
              </span>
            </button>)}
        </div>}

      {/* Archive Info Banner */}
      {view === 'archive' && <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 flex items-center gap-3" data-id="element-1481">
          <Archive size={20} className="text-slate-500 shrink-0" data-id="element-1482" />
          <p className="text-slate-400 text-sm" data-id="element-1483">
            Closed tickets are stored here for review and record-keeping. These
            tickets have been resolved and closed by staff.
          </p>
        </div>}

      {/* Ticket List */}
      <div className="grid grid-cols-1 gap-4" data-id="element-1484">
        {filteredTickets.map((ticket, i) => <motion.div key={ticket.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.05
      }} className={`bg-slate-950 border rounded-xl overflow-hidden transition-colors ${expandedTicket === ticket.id ? 'border-fox-500/50' : 'border-slate-800 hover:border-slate-700'}`} data-id="element-1485">
            {/* Ticket Header */}
            <div className="p-5 cursor-pointer" onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)} data-id="element-1486">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1487">
                <div className="flex-1" data-id="element-1488">
                  <div className="flex items-center gap-3 mb-2" data-id="element-1489">
                    <span className="text-slate-500 font-mono text-sm" data-id="element-1490">
                      {ticket.id}
                    </span>
                    <h3 className="text-lg font-bold text-white" data-id="element-1491">
                      {ticket.subject}
                    </h3>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-400" data-id="element-1492">
                    <span className="flex items-center gap-1.5" data-id="element-1493">
                      <Clock size={14} data-id="element-1494" />
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </span>
                    {ticket.department && departments.length > 0 && <span className="flex items-center gap-1.5 text-slate-300" data-id="element-1495">
                        <Building2 size={14} data-id="element-1496" />
                        {departments.find(d => d.id === ticket.department)?.name || ticket.department}
                      </span>}
                    <span className="flex items-center gap-1.5" data-id="element-1497">
                      <MessageSquare size={14} data-id="element-1498" />
                      {ticket.messages.length} messages
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3" data-id="element-1499">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`} data-id="element-1500">
                    {ticket.priority.toUpperCase()}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`} data-id="element-1501">
                    {getStatusIcon(ticket.status)}
                    {ticket.status === 'in-progress' ? 'IN PROGRESS' : ticket.status.toUpperCase()}
                  </span>
                  <ChevronDown size={18} className={`text-slate-500 transition-transform ${expandedTicket === ticket.id ? 'rotate-180' : ''}`} data-id="element-1502" />
                </div>
              </div>
            </div>

            {/* Expanded Ticket Detail */}
            <AnimatePresence data-id="element-1503">
              {expandedTicket === ticket.id && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden" data-id="element-1504">
                  <div className="border-t border-slate-800" data-id="element-1505">
                    {/* Status & Assignment Controls */}
                    <div className="p-4 bg-slate-900/50 border-b border-slate-800 flex flex-wrap items-center gap-4" data-id="element-1506">
                      <div className="flex items-center gap-2" data-id="element-1507">
                        <span className="text-xs text-slate-500 font-medium" data-id="element-1508">
                          Status:
                        </span>
                        {(['open', 'in-progress', 'resolved', 'closed'] as const).map(status => <button key={status} onClick={e => {
                    e.stopPropagation();
                    handleStatusChange(ticket.id, status);
                  }} className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors border ${ticket.status === status ? getStatusColor(status) : 'border-slate-700 text-slate-500 hover:text-slate-300 hover:border-slate-600'}`} data-id="element-1509">
                            {status === 'in-progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
                          </button>)}
                      </div>

                      <div className="flex items-center gap-2 ml-auto" data-id="element-1510">
                        <span className="text-xs text-slate-500 font-medium" data-id="element-1511">
                          Assign:
                        </span>
                        <div className="space-y-2" data-id="element-1512">
                          <label className="text-sm font-medium text-slate-300" data-id="element-1513">
                            Assign To
                          </label>
                          <div className="relative" data-id="element-1514">
                            <select value={ticket.assignedTo || ''} onChange={e => handleAssign(ticket.id, e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-fox-500 appearance-none" data-id="element-1515">
                              <option value="" data-id="element-1516">Unassigned</option>
                              {/* Group staff by department suggestion */}
                              {ticket.department && departments.find(d => d.id === ticket.department)?.staffIds?.length > 0 && <optgroup label="Department Staff" data-id="element-1517">
                                    {staff.filter(s => departments.find(d => d.id === ticket.department)?.staffIds?.includes(s.id)).map(member => <option key={member.id} value={member.id} data-id="element-1518">
                                          {member.name} ({member.role})
                                        </option>)}
                                  </optgroup>}
                              <optgroup label="All Staff" data-id="element-1519">
                                {staff.map(member => <option key={member.id} value={member.id} data-id="element-1520">
                                    {member.name} ({member.role})
                                  </option>)}
                              </optgroup>
                            </select>
                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-1521" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Message Thread */}
                    <div className="p-5 space-y-4 max-h-80 overflow-y-auto" data-id="element-1522">
                      {ticket.messages.map(msg => <div key={msg.id} className={`flex gap-3 ${msg.role === 'staff' ? 'flex-row-reverse' : ''}`} data-id="element-1523">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${msg.role === 'staff' ? 'bg-fox-500/20 text-fox-500' : 'bg-blue-500/20 text-blue-500'}`} data-id="element-1524">
                            {msg.role === 'staff' ? 'S' : 'C'}
                          </div>
                          <div className={`max-w-[75%] ${msg.role === 'staff' ? 'text-right' : ''}`} data-id="element-1525">
                            <div className={`rounded-xl px-4 py-3 ${msg.role === 'staff' ? 'bg-fox-600/10 border border-fox-500/20' : 'bg-slate-900 border border-slate-800'}`} data-id="element-1526">
                              <p className="text-sm text-slate-200" data-id="element-1527">
                                {msg.content}
                              </p>
                            </div>
                            <div className={`flex items-center gap-2 mt-1 ${msg.role === 'staff' ? 'justify-end' : ''}`} data-id="element-1528">
                              <span className="text-xs text-slate-600" data-id="element-1529">
                                {msg.sender}
                              </span>
                              <span className="text-xs text-slate-700" data-id="element-1530">•</span>
                              <span className="text-xs text-slate-600" data-id="element-1531">
                                {formatDate(msg.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>)}
                    </div>

                    {/* Reply Box (only for non-closed tickets) */}
                    {ticket.status !== 'closed' && <div className="p-4 border-t border-slate-800 bg-slate-900/30" data-id="element-1532">
                        <div className="flex gap-3" data-id="element-1533">
                          <input type="text" value={expandedTicket === ticket.id ? replyText : ''} onChange={e => setReplyText(e.target.value)} onClick={e => e.stopPropagation()} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleReply(ticket.id);
                    }
                  }} placeholder="Type your reply..." className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white text-sm focus:border-fox-500 focus:outline-none" data-id="element-1534" />
                          <button onClick={e => {
                    e.stopPropagation();
                    handleReply(ticket.id);
                  }} disabled={!replyText.trim()} className="bg-fox-600 hover:bg-fox-500 disabled:opacity-40 text-white px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm" data-id="element-1535">
                            <Send size={14} data-id="element-1536" /> Reply
                          </button>
                        </div>
                        {ticket.status === 'resolved' && <p className="text-xs text-slate-500 mt-2" data-id="element-1537">
                            This ticket is resolved. You can close it to move it
                            to the archive.
                          </p>}
                      </div>}

                    {/* Closed ticket notice */}
                    {ticket.status === 'closed' && <div className="p-4 border-t border-slate-800 bg-slate-900/30 flex items-center gap-3" data-id="element-1538">
                        <Archive size={16} className="text-slate-500" data-id="element-1539" />
                        <p className="text-sm text-slate-500" data-id="element-1540">
                          This ticket is closed and archived. You can reopen it
                          by changing the status above.
                        </p>
                      </div>}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>)}

        {filteredTickets.length === 0 && <div className="text-center py-12 text-slate-500 bg-slate-950 rounded-xl border border-slate-800" data-id="element-1541">
            <div className="mb-3" data-id="element-1542">
              {view === 'archive' ? <Archive size={40} className="mx-auto text-slate-700" data-id="element-1543" /> : <MessageSquare size={40} className="mx-auto text-slate-700" data-id="element-1544" />}
            </div>
            {view === 'archive' ? 'No closed tickets in the archive yet.' : 'No tickets found for this filter.'}
          </div>}
      </div>
    </div>;
}